import React from "react";
import { FONTS, ELEMENTS, typographyCss } from "@/lib/typography";

const cell = "border border-neutral-300 bg-white px-2 py-1.5 text-xs outline-none focus:border-neutral-900 w-full";

function FontSelect({ value, onChange, placeholder, testId }: any) {
  return (
    <select value={value || ""} onChange={(e) => onChange(e.target.value)} className={cell} data-testid={testId}>
      <option value="">{placeholder}</option>
      {FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
    </select>
  );
}

function ColorPick({ value, onChange, testId }: any) {
  return (
    <span className="flex items-center gap-1.5">
      <input type="color" value={value || "#111111"} onChange={(e) => onChange(e.target.value)} className="h-7 w-9 border border-neutral-300" data-testid={testId} />
      {value ? <button type="button" onClick={() => onChange("")} className="text-[10px] underline text-neutral-500">reset</button> : <span className="text-[10px] text-neutral-500">default</span>}
    </span>
  );
}

function ElementRow({ e, v, up }: any) {
  const num = (k: string, ph: string, step = 1) => <input type="number" step={step} value={v[k] ?? ""} onChange={(x) => up({ [k]: x.target.value })} placeholder={ph} className={cell} data-testid={`typo-${e.k}-${k}`} />;
  return (
    <tr className="border-t border-neutral-100 align-middle" data-testid={`typo-row-${e.k}`}>
      <td className="py-2 pr-3 text-xs font-medium whitespace-nowrap">{e.label}</td>
      <td className="py-2 pr-2 min-w-[140px]"><FontSelect value={v.font} onChange={(f: string) => up({ font: f })} placeholder="Default" testId={`typo-${e.k}-font`} /></td>
      <td className="py-2 pr-2 w-20">{num("size", "auto")}</td>
      <td className="py-2 pr-2 w-20">{num("size_mobile", "auto")}</td>
      <td className="py-2 pr-2 w-24"><select value={v.weight || ""} onChange={(x) => up({ weight: x.target.value })} className={cell} data-testid={`typo-${e.k}-weight`}><option value="">auto</option>{[300, 400, 500, 600, 700].map((w) => <option key={w}>{w}</option>)}</select></td>
      <td className="py-2 pr-2 w-20">{num("spacing", "auto", 0.01)}</td>
      <td className="py-2 pr-2 w-28"><select value={v.case || ""} onChange={(x) => up({ case: x.target.value })} className={cell} data-testid={`typo-${e.k}-case`}><option value="">auto</option><option value="uppercase">UPPERCASE</option><option value="none">As typed</option><option value="capitalize">Title Case</option></select></td>
      <td className="py-2"><ColorPick value={v.color} onChange={(c: string) => up({ color: c })} testId={`typo-${e.k}-color`} /></td>
    </tr>
  );
}

export function TypographyEditor({ t, onChange }: { t: any; onChange: (v: any) => void }) {
  const v = t || {};
  const set = (k: string, val: any) => onChange({ ...v, [k]: val });
  const up = (k: string, patch: any) => set("elements", { ...(v.elements || {}), [k]: { ...(v.elements?.[k] || {}), ...patch } });
  return (
    <section className="bg-white border border-neutral-200 p-6 space-y-6" data-testid="typography-editor">
      <div className="flex items-start justify-between gap-4">
        <div><p className="eyebrow text-[10px] text-neutral-500">Fonts, sizes & text colours</p><p className="text-xs text-neutral-600 mt-1">Leave anything blank to keep the current design. Sizes are in pixels; letter spacing in em (e.g. 0.08).</p></div>
        <button type="button" onClick={() => onChange({})} className="text-[11px] uppercase tracking-wider underline whitespace-nowrap" data-testid="typo-reset-all">Reset all</button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <label className="text-xs space-y-1"><span className="block text-neutral-600">Heading font</span><FontSelect value={v.heading_font} onChange={(f: string) => set("heading_font", f)} placeholder="Cormorant Garamond (default)" testId="typo-heading-font" /></label>
        <label className="text-xs space-y-1"><span className="block text-neutral-600">Body font</span><FontSelect value={v.body_font} onChange={(f: string) => set("body_font", f)} placeholder="Jost (default)" testId="typo-body-font" /></label>
        <label className="text-xs space-y-1"><span className="block text-neutral-600">All headings colour</span><ColorPick value={v.heading_color} onChange={(c: string) => set("heading_color", c)} testId="typo-heading-color" /></label>
        <label className="text-xs space-y-1"><span className="block text-neutral-600">Link hover colour</span><ColorPick value={v.link_hover_color} onChange={(c: string) => set("link_hover_color", c)} testId="typo-link-hover-color" /></label>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead><tr className="text-[10px] uppercase tracking-wider text-neutral-500"><th className="pb-2">Element</th><th className="pb-2">Font</th><th className="pb-2">Size</th><th className="pb-2">Mobile</th><th className="pb-2">Weight</th><th className="pb-2">Spacing</th><th className="pb-2">Case</th><th className="pb-2">Colour</th></tr></thead>
          <tbody>{ELEMENTS.map((e) => <ElementRow key={e.k} e={e} v={v.elements?.[e.k] || {}} up={(p: any) => up(e.k, p)} />)}</tbody>
        </table>
      </div>
      <TypographyPreview t={v} />
    </section>
  );
}

function TypographyPreview({ t }: { t: any }) {
  return (
    <div className="storefront border border-dashed border-neutral-300 p-6 space-y-3 bg-[var(--c-bg)]" data-testid="typo-preview">
      <style>{typographyCss(t).replace(/\.storefront/g, "[data-testid='typo-preview'].storefront")}</style>
      <p className="eyebrow">{ELEMENTS[4].sample}</p>
      <h1 className="font-display text-4xl">{ELEMENTS[0].sample}</h1>
      <h2 className="font-display text-2xl">{ELEMENTS[1].sample}</h2>
      <h3 className="font-display text-lg">{ELEMENTS[2].sample}</h3>
      <p className="text-sm">{ELEMENTS[3].sample} <a href="#typo" className="underline">A sample link</a></p>
    </div>
  );
}
